import { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/styles";
import { Grid, Box, FormControl, FormLabel, FormGroup, FilledInput, Tooltip, IconButton, Icon } from "@material-ui/core";
import { DataGrid } from "@material-ui/data-grid";
import { useLocation, Link } from "react-router-dom";

import Loader from "../../common/loader";
import Crud from "../../common/crud";
import NotFound from "../../common/not-found";
import ViewTitle from "../../common/view-title";
import Trash from "../../common/trash";

import AuthContext from "../../context";
import { del, get, post, put } from "../../utils/api-services";
import helpers from "../../utils/helpers";

const useStyles = makeStyles(() => ({
	root: {
		display: "flex",
		flexDirection: "column",
		flex: 1,
		paddingBottom: 20,
	},
	tableContainer: {
		width: "100%",
	},
}));

const Customers = () => {
	const { showToast, signOut, showLoader } = useContext(AuthContext);
	const classes = useStyles();
	const { pathname } = useLocation();
	const [state, setState] = useState({
		loading: true,
		customers: [],
		customer: {
			idff_customer: 0,
			code: '',
			name: "",
			address: "",
		},
		permissions: {},
	});

	useEffect(() => {
		window.document.title = "Customers";

		validatePermissions();
	}, []);

	const validatePermissions = () => {
		const permissions = helpers.getPermissions(pathname);
		setState((prev) => ({ ...prev, permissions }));

		permissions.read_attr ? makeRequest() : setState((prev) => ({ ...prev, loading: false }));
	};

	const makeRequest = async () => {
		showLoader(true);
		const response = await get("/farmForest/customer");
		if (response.status === 200) {
			setState((prev) => ({ ...prev, loading: false, customers: response?.data ?? [] }));
		} else {
			console.log("[Customers] makeRequest error", response);
			helpers.failRequest(response, showToast, signOut);
		}
		showLoader(false);
	};

	const handleChange = ({ target }) => setState((prev) => ({ ...prev, customer: { ...prev.customer, [target.name]: target.value } }));

	const validateForm = (type) => {
		const { idff_customer, name } = state.customer;
		const { create_attr, update_attr, delete_attr } = state.permissions;

		switch (type) {
			case "update":
				if (!update_attr) {
					showToast('You have no permissions for this action', 'error', 6500);
					return false;
				}
				const customer = state.customers.find(value => value.idff_customer === idff_customer);
				if (!customer) {
					showToast('Select some customer', 'error');
					return false;
				}
				const before = JSON.stringify(customer);
				const after = JSON.stringify(state.customer);

				if (before === after) {
					showToast('Update some field', 'error');
					return false;
				}
				if (!name.trim()) {
					showToast('Name field is required', 'error');
					return false;
				}

				return true;
			case "store":
				if (!create_attr) {
					showToast('You have no permissions for this action', 'error', 6500);
					return false;
				}
				if (!name.trim()) {
					showToast('Name field is required', 'error');
					return false;
				}

				return true;
			case "delete":
				if (!delete_attr) {
					showToast('You have no permissions for this action', 'error', 6500);
					return false;
				}
				if (!idff_customer) {
					showToast('Select some customer', 'error');
					return false;
				}

				return window.confirm('Are you sure you want to delete this customer?');
			default:
				break;
		}
	};

	const clear = () => setState((prev) => ({ ...prev, customer: { ...prev.customer, idff_customer: 0, code: '', name: "", address: "" } }));


	const store = async () => {
		const cond = validateForm("store");

		if (cond) {
			showLoader(true);
			const response = await post("/farmForest/customer/store", state.customer);
			if (response.status === 200) {
				setState((prev) => ({ ...prev, customers: response?.data ?? [] }));
				showToast(response.message);
			} else {
				console.log("[Customers] store error", response);
				helpers.failRequest(response, showToast, signOut);
				showLoader(false);

				return false;
			}
			showLoader(false);
		}

		return cond;
	};

	const update = async () => {
		const { idff_customer } = state.customer;
		const cond = validateForm("update");

		if (cond) {
			showLoader(true);
			const response = await put(`/farmForest/customer/${idff_customer}`, state.customer);
			if (response.status === 200) {
				setState((prev) => ({ ...prev, customers: response?.data ?? [] }));
				showToast(response.message);
			} else {
				console.log("[Customers] update error", response);
				helpers.failRequest(response, showToast, signOut);
			}
			showLoader(false);
		}
	};

	const destroy = async () => {
		const { idff_customer } = state.customer;
		const cond = validateForm("delete");

		if (cond) {
			showLoader(true);
			const response = await del(`/farmForest/customer/${idff_customer}`);
			if (response.status === 200) {
				setState((prev) => ({ ...prev, customers: response?.data ?? [] }));
				showToast(response.message);
			} else {
				console.log("[Customers] destroy error", response);
				helpers.failRequest(response, showToast, signOut);
			}
			showLoader(false);
		}
	};

	const renderCell = ({ row }) => {
		return (
			<Link to={`/history/ff_customer/${row.idff_customer}`} target="_blank">
				<Tooltip title="Show history" placement="top">
					<IconButton>
						<Icon>{"history"}</Icon>
					</IconButton>
				</Tooltip>
			</Link>
		);
	};

	const columns = [
		{ field: 'code', headerName: 'Code', flex: 0.5 },
		{ field: "name", headerName: "Name", flex: 1 },
		{ field: "history", headerName: "Actions", flex: 0.3, renderCell },
	];

	if (state.loading) return <Loader />;
	if (!state.permissions.read_attr) return <NotFound />;

	return (
		<div className={classes.root}>
			<ViewTitle title="Customers" />
			<Grid container>
				<Grid item xs={5} md={3}>
					<FormGroup>
						<FormLabel>{"Code"}</FormLabel>
						<FormControl variant="filled" component={Box} width="100%">
							<FilledInput disabled placeholder="Code (automatic)" value={state.customer?.code ?? '?'} />
						</FormControl>
					</FormGroup>
				</Grid>
				<Grid item xs={7} md={5}>
					<FormGroup>
						<FormLabel>{"Name"}</FormLabel>
						<FormControl variant="filled" component={Box} width="100%">
							<FilledInput autoComplete="off" type="text" placeholder="Name" name="name" value={state.customer.name} onChange={handleChange} />
						</FormControl>
					</FormGroup>
				</Grid>
			</Grid>

			<Crud permissions={state.permissions} create={{ create: clear, store }} update={update} destroy={destroy} cancel={clear} />

			{state.permissions.delete_attr && <Trash table="ff_customer" id="idff_customer" onRefresh={makeRequest} />}

			<div className={classes.tableContainer}>
				<DataGrid
					rows={state.customers}
					columns={columns}
					pageSize={45}
					density="compact"
					autoHeight
					getRowId={(row) => row.idff_customer}
					loading={state.loading}
					onRowClick={({ row }) => setState((prev) => ({ ...prev, customer: row }))}
				/>
			</div>
		</div>
	);
};

export default Customers;
